import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useIntl } from "react-intl";
import {
  AdminSkuDeliverableRequest,
  IAdminSkuDeliverableRequest,
  SkuDeliverableInfo,
  Skus,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import SkuDeliveryMethodCommonForm from "./SkuDeliveryMethodCommonForm";
import { SkuFormProps } from "types/sku-related-types";
import { DialogContent } from "@mui/material";
import SkuDeliveryMediaItemUpdateForm from "./SkuDeliveryMediaItemUpdateForm";
import { mediaItemFormPropsMap } from "./SkuDeliveryMediaItemCreateForm";

type SkuDeliveryMethodCreateProps = {
  sku: Skus;
  skuDeliveryMethod: SkuDeliverableInfo;
  onCancel?: () => void;
  onSkuDeliveryMethodUpdated: () => void;
} & SkuFormProps;

const SkuDeliveryMethodUpdateForm = ({
  sku,
  skuDeliveryMethod,
  onCancel,
  onSkuDeliveryMethodUpdated,
  onDirty,
  onClean,
  ...rest
}: SkuDeliveryMethodCreateProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();

  const onCommonFormSubmit = async (
    values: IAdminSkuDeliverableRequest
  ) => {
    try {
      const response = await registerPromise(
        skuService.saveSkuDeliveryMethod(
          sku.id!,
          AdminSkuDeliverableRequest.fromJS(values)
        )
      );

      onSkuDeliveryMethodUpdated();
      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const mediaItemFormProps =
    skuDeliveryMethod.deliveryMethodId &&
    mediaItemFormPropsMap[skuDeliveryMethod.deliveryMethodId];

  return (
    <>
      <DialogContent>{skuDeliveryMethod.deliveryMethodName}</DialogContent>

      {!!skuDeliveryMethod.deliveryMethodId && !mediaItemFormProps && (
        <SkuDeliveryMethodCommonForm
          onSubmit={onCommonFormSubmit}
          onCancel={onCancel}
          onDirty={onDirty}
          onClean={onClean}
          sku={sku}
          skuDeliveryMethod={skuDeliveryMethod}
          submitLabel={t({ id: "skuDeliveryMethodUpdateButtonLabel" })}
          {...rest}
        />
      )}

      {!!skuDeliveryMethod.deliveryMethodId && !!mediaItemFormProps && (
        <SkuDeliveryMediaItemUpdateForm
          {...mediaItemFormProps}
          onUpdated={onSkuDeliveryMethodUpdated}
          onCancel={() => onCancel && onCancel()}
          deliverableId={skuDeliveryMethod.deliverableId}
        />
      )}
    </>
  );
};

export default SkuDeliveryMethodUpdateForm;
